import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { SUPPORTED_LANGS } from '../i18n/translationsLazy';

const SITE = 'https://sitgesvibes.com';

/**
 * Per-route meta tags (title / description / canonical / hreflang / OG).
 * Each page passes its own copy in; we handle the URL plumbing + alternates.
 *
 * Canonicals follow the ?lang=xx pattern that matches our existing sitemap
 * and index.html so we don't fork the URL strategy.
 */
export function SEOHead({ title, description, image, jsonLd, noindex = false }) {
  const { pathname, search } = useLocation();
  const { lang } = useLanguage();

  // Build canonical for the current URL ignoring query strings except `lang`
  // (everything else — ?search=, ?category= — is a filtered view, not a
  // canonical page).
  const params = new URLSearchParams(search);
  const canonicalParams = new URLSearchParams();
  if (params.get('lang')) canonicalParams.set('lang', params.get('lang'));
  if (params.get('category')) canonicalParams.set('category', params.get('category'));
  if (params.get('location')) canonicalParams.set('location', params.get('location'));

  const qs = canonicalParams.toString();
  const canonical = `${SITE}${pathname}${qs ? `?${qs}` : ''}`;

  // hreflang alternates — same pathname + filter params, only lang changes.
  const buildAlt = (altLang) => {
    const p = new URLSearchParams();
    canonicalParams.forEach((v, k) => {
      if (k !== 'lang') p.set(k, v);
    });
    if (altLang !== 'en') p.set('lang', altLang);
    const qp = p.toString();
    return `${SITE}${pathname}${qp ? `?${qp}` : ''}`;
  };

  const ogImage = image || `${SITE}/og-image.jpg`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* hreflang: x-default → EN canonical; one per supported language */}
      <link rel="alternate" hrefLang="x-default" href={buildAlt('en')} />
      {SUPPORTED_LANGS.map((code) => (
        <link key={code} rel="alternate" hrefLang={code} href={buildAlt(code)} />
      ))}

      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data (e.g. TouristAttraction for experience
          detail pages, ItemList for listings, BreadcrumbList for nav path)
          — unlocks Google rich results: stars, price, breadcrumbs, etc.
          Pages may pass either a single object or an array of blocks; each
          block is emitted as its own <script> tag which is the pattern
          Google prefers over @graph for unrelated types. */}
      {jsonLd && (
        Array.isArray(jsonLd)
          ? jsonLd.filter(Boolean).map((block, i) => (
              <script key={i} type="application/ld+json">
                {JSON.stringify(block)}
              </script>
            ))
          : (
            <script type="application/ld+json">
              {JSON.stringify(jsonLd)}
            </script>
          )
      )}
    </Helmet>
  );
}

export default SEOHead;
